import { v4 as uuid } from 'uuid';

import { AnalyticsEventMap, AnalyticsEventType, DefaultData } from '../events';
import { BrightcoveDriverConfig, Driver } from '../types';

type BCEventID =
    | 'video_impression'
    | 'play_request'
    | 'video_view'
    | 'video_engagement'
    | 'error';

/** Params that remain the same across all requests */
type BCConstantParams = {
    domain: 'videocloud';
    account: string;
    /** This is in the docs but doesn't seem to be sent from BC's own player */
    application: string;
    // The following are undocumented, but sent from the player on BFI's website:
    platform: 'easeltv-web';
    platform_version: string;
    player: 'easeltv-web';
    player_name: 'Easel TV Web';
};

/** Params that must always be present but can change between requests */
type BCVariableParams = {
    /**
     * This is the session identifier. The session is essentially one view of a page or app view
     * that has a player in it, for as long as that lasts. The value should be constant for the
     * duration of the session and sent for all events. It should be as close as is possible to
     * a globally unique identifier (GUID). If there are collisions, the two sessions may be
     * discarded as invalid if they cannot be disentangled.
     */
    session: string;
    destination: string;
    source: string;
    video: string;
    video_name: string;
    /**
     * The duration of the video in seconds. Docs say this is just for video_engagement events but
     * based on the website behaviour apparently not.
     */
    video_duration: number;
    user: string;
    // The following are undocumented, but sent from the player on BFI's website:
    /** Looks like the client timestamp in milliseconds */
    time: number;
    /** URL of stream - e.g. DASH manifest */
    media_url: string;
    /** like "3_0" where the first number is incremented with each request */
    seq: string;
};

type BCVideoImpression = {
    id: Extract<BCEventID, 'video_impression'>;
    params: BCConstantParams & BCVariableParams;
};
type BCPlayRequest = {
    id: Extract<BCEventID, 'play_request'>;
    params: BCConstantParams & BCVariableParams;
};
type BCVideoView = {
    id: Extract<BCEventID, 'video_view'>;
    params: BCConstantParams &
        BCVariableParams & {
            start_time_ms: number;
            // The following is undocumented, but sent from the player on BFI's website:
            load_time_ms: number;
        };
};
type BCVideoEngagement = {
    id: Extract<BCEventID, 'video_engagement'>;
    params: BCConstantParams &
        BCVariableParams & {
            /**
             * The range of the video viewed for video_engagement events in the format
             * StartSecond..EndSecond (the StartSecond and EndSecond values must be whole numbers
             * [integers]) - range can be left out of an engagement event to show that during the
             * period covered by the event, there was no viewing activity. (for example, when
             * there is only re-buffering activity).
             * Example "10..19"
             */
            range: string;
            // The following are undocumented, but sent from the player on BFI's website:
            // TODO: Player height/width would requre some work given that it resizes - maybe a future addition
            // player_height: number;
            // player_width: number;
        };
};
type BCError = {
    id: Extract<BCEventID, 'error'>;
    params: BCConstantParams &
        BCVariableParams & {
            error_code: string;
        };
};

type BCEvent =
    | BCVideoImpression
    | BCPlayRequest
    | BCVideoView
    | BCVideoEngagement
    | BCError;

function BCAnalyticsSender() {
    // Needs to send using img tags according to Brightcove docs, so this'll be fun...

    let div: HTMLElement | undefined;

    const createContainerDiv = () => {
        const id = '__brightcoveanalyticssender__';

        // If this all got re-instantiated somehow, reuse the existing div
        const existing = document.getElementById(id);
        if (existing) return existing;

        const el: HTMLElement = document.createElement('div');
        el.id = id;
        el.style.position = 'absolute';
        el.style.display = 'none';
        document.body.appendChild(el);
        return el;
    };

    const createMomentaryImgTag = (url: string) => {
        if (!div) div = createContainerDiv();
        const img: HTMLImageElement = document.createElement('img');
        img.src = url;
        div.appendChild(img);
        window.setTimeout(() => div?.removeChild(img), 500);
    };

    const doRequest = (url: string) => {
        createMomentaryImgTag(url);
    };

    return { doRequest };
}

function eventIsSupported(type: string): boolean {
    switch (type) {
        case 'playback.error':
        case 'playback.created':
        case 'playback.loading':
        case 'playback.loaded':
        case 'playback.engagement':
            return true;
        default:
            return false;
    }
}

export const BrightcoveDriver = ({
    accountId,
}: BrightcoveDriverConfig): Driver => {
    let enabled = false;
    /** We should only send BC analytics events in the correct order */
    const expectedSequence: Readonly<AnalyticsEventType[]> = Object.freeze([
        'playback.created',
        'playback.loading',
        'playback.loaded',
        'playback.engagement',
    ]);
    let sequencePosition = 0;
    let requestCounter = 0;
    let videoImpressionTimestamp = 0;
    let playRequestTimestamp = 0;
    let lastEngagementPingSecsTracked = 0;
    let lastVideoId = '';
    let lastSentEvent: BCEvent | null = null;

    const { protocol } = window.location;
    const baseUrl = `${protocol}//metrics.brightcove.com/tracker/v2/?`;
    const sender = BCAnalyticsSender();

    const commonParams: BCConstantParams &
        Pick<BCVariableParams, 'user' | 'session'> = {
        account: accountId,
        application: '',
        domain: 'videocloud',
        session: '',
        user: '',
        platform: 'easeltv-web',
        platform_version: '',
        player: 'easeltv-web',
        player_name: 'Easel TV Web',
    };

    const convertVideoEvent = <T extends AnalyticsEventType>(
        payload: AnalyticsEventMap[T],
    ): BCEvent | null => {
        switch (payload.type) {
            case 'playback.error':
            case 'playback.created':
            case 'playback.loading':
            case 'playback.loaded':
            case 'playback.engagement':
                // narrowing the type so we can extract common params
                break;
            default:
                return null;
        }

        const isError = payload.type === 'playback.error';

        if (payload.brightcoveVideoId) lastVideoId = payload.brightcoveVideoId;

        const varParams: Omit<BCVariableParams, 'user' | 'session'> = {
            destination: window.location.href,
            source: window.location.href,
            time: Date.now(),
            video: lastVideoId || '',
            video_name: payload.productName,
            video_duration: isError ? 0 : Math.round(payload.videoDuration),
            media_url: isError
                ? ''
                : payload.manifestUrl || lastSentEvent?.params?.media_url || '',
            seq: `${requestCounter}_0`,
        };

        switch (payload.type) {
            case 'playback.created':
                videoImpressionTimestamp = Date.now();
                return {
                    id: 'video_impression',
                    params: {
                        ...commonParams,
                        ...varParams,
                    },
                };
            case 'playback.loading':
                playRequestTimestamp = Date.now();
                return {
                    id: 'play_request',
                    params: {
                        ...commonParams,
                        ...varParams,
                    },
                };
            case 'playback.loaded': {
                const now = Date.now();
                return {
                    id: 'video_view',
                    params: {
                        ...commonParams,
                        ...varParams,
                        start_time_ms: now - videoImpressionTimestamp,
                        load_time_ms: now - playRequestTimestamp,
                    },
                };
            }
            case 'playback.engagement': {
                const secs = Math.round(payload.videoPosition);
                const range = `${lastEngagementPingSecsTracked}..${secs}`;
                lastEngagementPingSecsTracked = secs;
                return {
                    id: 'video_engagement',
                    params: {
                        ...commonParams,
                        ...varParams,
                        range,
                    },
                };
            }
            case 'playback.error':
                return {
                    id: 'error',
                    params: {
                        ...commonParams,
                        ...varParams,
                        error_code: payload.reason,
                    },
                };
            default:
                return null;
        }
    };

    const genUrl = (event: BCEvent): string => {
        const paramParts = Object.entries(event.params).map(
            ([k, v]) => `${k}=${encodeURIComponent(v)}`,
        );
        return `${baseUrl}event=${event.id}&${paramParts.join('&')}`;
    };

    const setDefaultData = (data: DefaultData) => {
        commonParams.application = `[Easel TV] ${data.appName}`;
        commonParams.platform_version = data.appVersion;
        commonParams.user = data.userId ?? '';
    };

    return {
        enable() {
            enabled = true;
        },
        disable() {
            enabled = false;
        },
        async initialize() {
            // no-op
        },
        setDefaultData,
        trigger(payload) {
            // These just modify common params - allow this even if not currently enabled
            if (
                payload.type === 'account.signed-in' ||
                payload.type === 'account.registration.completed'
            ) {
                commonParams.user = payload.userId || '';
                return;
            }
            if (payload.type === 'account.signed-out') {
                commonParams.user = '';
                return;
            }

            // Bail out early
            if (!enabled || !accountId) return;
            if (!eventIsSupported(payload.type)) return;

            if (payload.type === 'playback.created') {
                // New session, reset stuff
                commonParams.session = uuid();
                sequencePosition = 0;
                lastEngagementPingSecsTracked = 0;
                requestCounter = 0;
            }

            const expectedType = expectedSequence[sequencePosition];

            let ev: BCEvent | null = null;
            if (
                payload.type === 'playback.error' &&
                sequencePosition > 0 &&
                !!lastSentEvent
            ) {
                ev = {
                    id: 'error',
                    params: {
                        ...lastSentEvent.params,
                        error_code: payload.reason,
                    },
                };
            } else if (expectedType !== payload.type) {
                // Bail out if we received an event that's out of sequence,
                // doing that could allegedly mess up BC data.
                return;
            }
            // advance sequence to the last position but not beyond (we can get multiple
            // engagement events)
            sequencePosition = Math.min(
                sequencePosition + 1,
                expectedSequence.length - 1,
            );

            ev = convertVideoEvent(payload);
            if (!ev) return;
            lastSentEvent = ev;
            const url = genUrl(ev);
            sender.doRequest(url);

            requestCounter += 1;
        },
        // this appears to be unused but it's in the interface def
        updateDefaultData: setDefaultData,
    };
};
